"use client";

import { useEffect, useRef, useState } from "react";
import { FileArchive, Loader2, Upload } from "lucide-react";

import { readSkillError } from "@/components/command-skills/skill-api";
import { validateSkillUpload } from "@/components/command-skills/skill-utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const EXAMPLE_SKILL_MD = `---
name: campaign-report
description: Build the weekly sales campaign report. Use when the user asks for campaign performance.
---
## Steps
1. Fetch the campaign stats
2. Run scripts/build_report.py
3. Save the workbook in assets/template.xlsx format`;

interface UploadSkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "version";
  pending: boolean;
  error: string | null;
  onSubmit: (file: File, onProgress: (percent: number) => void) => void;
}

export function UploadSkillDialog({
  open,
  onOpenChange,
  mode,
  pending,
  error,
  onSubmit,
}: UploadSkillDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [explainerOpen, setExplainerOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setLocalError(null);
      setProgress(null);
      setDragOver(false);
    }
  }, [open]);

  const accept = mode === "create" ? ".zip,.skill,.md" : ".zip,.skill";
  const message = localError || error;

  const choose = (next: File | null) => {
    if (!next) return;
    const validation = validateSkillUpload(next, mode);
    setFile(validation ? null : next);
    setLocalError(validation);
    setProgress(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
        <DialogContent
          className={cn(
            "gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-lg sm:rounded-2xl",
            pending && "[&>button]:pointer-events-none [&>button]:opacity-40",
          )}
          onPointerDownOutside={(event) => pending && event.preventDefault()}
          onEscapeKeyDown={(event) => pending && event.preventDefault()}
        >
          <DialogHeader className="space-y-1 border-b border-border/70 px-6 py-5 text-left">
            <DialogTitle className="text-base font-bold tracking-tight">
              {mode === "create" ? "Upload a skill" : "Upload new version"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {mode === "create"
                ? "A .zip, .skill, or SKILL.md file."
                : "This replaces every file. The skill name stays the same."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-6 py-5">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                if (!pending) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragOver(false);
                if (pending) return;
                choose(event.dataTransfer.files?.[0] ?? null);
              }}
              className={cn(
                "flex w-full items-center gap-3 rounded-2xl border border-dashed px-4 py-4 text-left transition-colors",
                dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/20",
                pending && "pointer-events-none opacity-70",
              )}
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-background text-primary shadow-sm">
                {file ? <FileArchive className="size-4" /> : <Upload className="size-4" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">
                  {file ? file.name : "Drop a file here"}
                </span>
                <span className="block text-[11px] text-muted-foreground">
                  {file
                    ? "Ready to upload"
                    : mode === "create"
                      ? "or click to browse · .zip, .skill, or .md · 30 MB max"
                      : "or click to browse · .zip or .skill · 30 MB max"}
                </span>
              </span>
            </button>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              className="sr-only"
              onChange={(event) => {
                choose(event.target.files?.[0] ?? null);
                event.target.value = "";
              }}
            />

            {mode === "create" ? (
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                The package needs a SKILL.md file, at the top or inside one folder. references, scripts, and assets are usual, not required.{" "}
                <button
                  type="button"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                  onClick={() => setExplainerOpen(true)}
                >
                  What goes in a skill?
                </button>
              </p>
            ) : null}

            {pending && progress !== null ? (
              <div className="space-y-1.5">
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-muted-foreground">Uploading… {progress}%</p>
              </div>
            ) : null}

            {message ? <p className="text-xs font-medium text-destructive">{message}</p> : null}
          </div>

          <DialogFooter className="border-t border-border/70 px-6 py-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-full px-4 text-xs font-semibold"
              disabled={pending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-9 rounded-full px-5 text-xs font-semibold"
              disabled={!file || pending}
              onClick={() => {
                if (!file) return;
                setProgress(0);
                onSubmit(file, setProgress);
              }}
            >
              {pending ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : null}
              {mode === "create" ? "Upload" : "Replace files"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={explainerOpen} onOpenChange={setExplainerOpen}>
        <DialogContent className="max-h-[85vh] gap-0 overflow-hidden overflow-y-auto rounded-2xl p-0 sm:max-w-lg sm:rounded-2xl">
          <DialogHeader className="space-y-1 border-b border-border/70 px-6 py-5 text-left">
            <DialogTitle className="text-base font-bold tracking-tight">What goes in a skill?</DialogTitle>
            <DialogDescription className="text-xs">
              A folder of instructions that teaches the Command agent how to do one task.
            </DialogDescription>
          </DialogHeader>
          <div className="min-w-0 space-y-4 px-6 py-5">
          <div className="overflow-hidden rounded-xl border text-sm">
            <div className="grid grid-cols-[7.5rem_1fr] border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
              <span>Part</span>
              <span>What it is for</span>
            </div>
            {[
              ["SKILL.md", "Name, description, and step-by-step instructions. Required."],
              ["references/", "Extra docs the agent reads only when needed."],
              ["scripts/", "Code the agent runs."],
              ["assets/", "Files the scripts use, such as templates, fonts, and logos."],
            ].map(([part, copy]) => (
              <div key={part} className="grid grid-cols-[7.5rem_1fr] gap-2 border-b px-3 py-2 last:border-b-0">
                <span className="font-mono text-xs">{part}</span>
                <span className="text-muted-foreground">{copy}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            The agent sees the name and description in every chat, and reads the full instructions
            when a task matches. Write what it does and when to use it.
          </p>
          <pre className="whitespace-pre-wrap break-words rounded-xl bg-muted p-3 font-mono text-[11px] leading-relaxed">
            {EXAMPLE_SKILL_MD}
          </pre>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function skillUploadErrorMessage(error: unknown): string {
  return readSkillError(error, "Could not upload this skill").message;
}
